/**
 * TokenRefreshService - Token refresh lifecycle management
 *
 * Extracted from SecureAuthService to isolate the mechanical token refresh
 * lifecycle (lock acquisition, retry with backoff, HTTP refresh, 401 request
 * queueing) from the authentication boundary (login/logout/session).
 *
 * Not a singleton — instantiated by SecureAuthService via composition.
 */

import { tokenService } from './TokenService';
import { API_URL } from '@/core/config/urls';
import { authLogger } from '@/core/utils/logger';
import { getErrorMessage } from '@voley/shared-utils';
import { extractErrorMessage } from '@/shared/utils/error';
import { trackRefresh, trackAuthError } from '../utils/authTelemetry';

export type RetryableRequest = () => Promise<Response>;

export interface TokenRefreshCallbacks {
  onAuthLost: () => void;
  isLogoutInProgress: () => boolean;
}

interface QueuedRequest {
  request: RetryableRequest;
  resolve: (value: Response) => void;
  reject: (error: Error) => void;
}

const AUTH_ENDPOINTS = {
  jwtRefresh: '/auth/jwt/refresh',
};

export class TokenRefreshService {
  private requestQueue: QueuedRequest[] = [];
  private refreshRetryCount = 0;
  private readonly MAX_REFRESH_RETRIES = 3;
  private readonly REFRESH_RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff
  private localRefreshLock: Promise<void> | null = null;

  private readonly callbacks: TokenRefreshCallbacks;

  constructor(callbacks: TokenRefreshCallbacks) {
    this.callbacks = callbacks;

    // Attempt silent refresh before token expires
    tokenService.on('token:expiring', () => {
      this.refreshAccessToken().catch(error => {
        authLogger.error('[TokenRefreshService] Auto-refresh failed:', error);

        // If the refresh failed with a non-retryable auth error (401/403),
        // the session is definitively dead — redirect to login immediately
        // rather than leaving the user on a broken dashboard.
        if (this.isNonRetryableError(error)) {
          authLogger.warn('[TokenRefreshService] Session expired during background refresh, redirecting to login');
          this.callbacks.onAuthLost();
        }
      });
    });
  }

  /**
   * Refresh access token using refresh token cookie with intelligent retry.
   * Uses local lock to prevent TOCTOU race condition between check and set.
   */
  async refreshAccessToken(): Promise<void> {
    // First check local lock (this prevents the TOCTOU race)
    if (this.localRefreshLock) {
      authLogger.info('[TokenRefreshService] Refresh already in progress (local lock), waiting...');
      return this.localRefreshLock;
    }

    // Then check TokenService (for cross-component coordination)
    const existingRefresh = tokenService.getRefreshPromise();
    if (existingRefresh) {
      authLogger.info('[TokenRefreshService] Refresh already in progress (token service), waiting...');
      return existingRefresh;
    }

    // Create the lock promise IMMEDIATELY before any async work
    // This closes the TOCTOU window
    this.localRefreshLock = (async () => {
      try {
        // Reset retry count for new refresh attempt
        this.refreshRetryCount = 0;

        // Create and register the refresh promise
        const refreshPromise = this.performTokenRefreshWithRetry();
        tokenService.setRefreshPromise(refreshPromise);

        await refreshPromise;
      } finally {
        // Clear local lock when done (success or failure)
        this.localRefreshLock = null;
      }
    })();

    return this.localRefreshLock;
  }

  /**
   * Handle 401 response with retry logic.
   * Queues the request and triggers a refresh if one isn't already in progress.
   */
  async handle401Response(retryRequest: RetryableRequest): Promise<Response> {
    authLogger.info('[TokenRefreshService] Handling 401 response');

    // Check if we have too many queued requests (circuit breaker pattern)
    if (this.requestQueue.length > 10) {
      authLogger.warn('[TokenRefreshService] Too many queued requests, clearing queue and forcing logout');
      this.abortPendingRequests();
      this.callbacks.onAuthLost();
      throw new Error('Too many authentication requests, please try again');
    }

    // Check if we're already processing a refresh to prevent multiple refreshes
    if (tokenService.getRefreshPromise()) {
      authLogger.info('[TokenRefreshService] Refresh already in progress, queuing request');
    } else {
      // Start a new refresh
      authLogger.info('[TokenRefreshService] Starting new token refresh');
    }

    // Queue the request
    return new Promise((resolve, reject) => {
      // Add timeout to prevent infinite waiting
      const timeoutId = setTimeout(() => {
        authLogger.warn('[TokenRefreshService] Request timeout in queue, forcing logout');
        this.callbacks.onAuthLost();
        reject(new Error('Authentication timeout'));
      }, 10000); // 10 second timeout

      this.requestQueue.push({
        request: retryRequest,
        resolve: (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        }
      });

      // Attempt token refresh
      this.refreshAccessToken().catch(error => {
        // If refresh fails, authentication is lost
        authLogger.error('[TokenRefreshService] Cannot recover from 401:', error);
        clearTimeout(timeoutId);
        this.callbacks.onAuthLost();
      });
    });
  }

  /**
   * Reject and clear all queued requests.
   * Called externally when authentication is lost.
   */
  abortPendingRequests(): void {
    authLogger.info(`[TokenRefreshService] Aborting ${this.requestQueue.length} queued requests`);
    const queue = [...this.requestQueue];
    this.requestQueue = [];

    const error = new Error('Authentication session cleared');
    for (const { reject } of queue) {
      try {
        reject(error);
      } catch {
        // Ignore errors from already-settled promises
      }
    }
  }

  /**
   * Get the number of queued requests (for diagnostics).
   */
  getQueuedRequestCount(): number {
    return this.requestQueue.length;
  }

  // Private

  /**
   * Perform token refresh with exponential backoff retry.
   */
  private async performTokenRefreshWithRetry(): Promise<void> {
    while (this.refreshRetryCount < this.MAX_REFRESH_RETRIES) {
      try {
        await this.performTokenRefresh();
        // Success - reset retry count
        this.refreshRetryCount = 0;
        return;
      } catch (error) {
        this.refreshRetryCount++;

        // Check if error is non-retryable
        if (this.isNonRetryableError(error)) {
          authLogger.error('[TokenRefreshService] Non-retryable refresh error', error);
          throw error;
        }

        if (this.refreshRetryCount >= this.MAX_REFRESH_RETRIES) {
          authLogger.error('[TokenRefreshService] Max refresh retries exceeded');
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = this.REFRESH_RETRY_DELAYS[this.refreshRetryCount - 1] || 4000;
        authLogger.warn(`[TokenRefreshService] Refresh failed, retry ${this.refreshRetryCount}/${this.MAX_REFRESH_RETRIES} in ${delay}ms`);

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Check if an error should not be retried.
   * Checks both error.status (if present) and error.message patterns.
   */
  private isNonRetryableError(error: unknown): boolean {
    const message = (error instanceof Error ? error.message : '').toLowerCase();
    const errorObj = error as { status?: number; statusCode?: number } | undefined;
    const status = errorObj?.status ?? errorObj?.statusCode;

    // Don't retry on explicit auth failures (status code or message-based)
    if (status === 401 || status === 403) return true;

    // Don't retry on user not found
    if (status === 404) return true;

    // Don't retry on invalid/expired refresh token (message-based detection)
    if (message.includes('invalid') || message.includes('expired')) return true;

    // Don't retry when performTokenRefresh explicitly signals auth failure
    if (message.includes('authentication failed')) return true;

    // Retry on network errors, timeouts, 5xx errors
    return false;
  }

  /**
   * Perform token refresh using JWT refresh endpoint.
   */
  private async performTokenRefresh(): Promise<void> {
    // Bail out if logout is in progress to prevent re-establishing auth state
    if (this.callbacks.isLogoutInProgress()) {
      authLogger.info('[TokenRefreshService] Skipping token refresh - logout in progress');
      return;
    }

    try {
      authLogger.info('[TokenRefreshService] Performing JWT token refresh');

      // Use the JWT refresh endpoint for actual token rotation
      const response = await fetch(`${API_URL}${AUTH_ENDPOINTS.jwtRefresh}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send httpOnly cookies
      });

      // Check for service unavailable
      if (response.status === 503) {
        throw new Error('Authentication service is temporarily unavailable');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        authLogger.error('[TokenRefreshService] JWT refresh failed', { status: response.status, error });

        // Attach HTTP status to the error so isNonRetryableError and
        // sessionManager.isTransientError can properly classify it
        const errMsg = response.status === 401 || response.status === 403
          ? (error.message || 'Authentication failed')
          : (error.message || `Refresh failed with status ${response.status}`);
        const refreshError = Object.assign(new Error(errMsg), {
          status: response.status,
          statusCode: response.status,
        });
        throw refreshError;
      }

      const result = await response.json();

      // Update token expiry tracking (cookies are set by backend)
      const expiresIn = result.data?.expiresIn || 14400; // Default to 4 hours
      tokenService.setTokens({
        accessToken: '', // Empty string - still use cookie-based auth
        expiresIn: expiresIn,
        tokenType: 'Bearer',
        isCookieAuth: true, // Explicit flag for cookie-based Stytch auth
      });

      // Process any queued requests
      this.processRequestQueue();

      authLogger.info('[TokenRefreshService] JWT refresh successful', {
        expiresIn: `${expiresIn}s (${(expiresIn / 3600).toFixed(1)}h)`
      });

      // Track successful refresh
      trackRefresh(true, { expiresIn });
    } catch (error: unknown) {
      authLogger.error('[TokenRefreshService] Session refresh error:', error);

      const errorMessage = getErrorMessage(error);
      // Preserve the original error (including .status/.statusCode) when possible
      const errorObj = error instanceof Error ? error : new Error(errorMessage);

      // Track failed refresh
      trackRefresh(false, {
        error: errorMessage
      });

      // Check if this is a service unavailable error
      if (errorMessage.includes('service unavailable') ||
        errorMessage.includes('temporarily unavailable')) {
        authLogger.warn('[TokenRefreshService] Service unavailable during refresh - clearing queue');
        trackAuthError('service_unavailable', { context: 'refresh' });
        // Don't redirect to login, just clear queue and let UI handle service unavailable
        this.abortPendingRequests();
        throw errorObj; // Re-throw to prevent further retries
      }

      // Only clear tokens for confirmed auth failures (401/403).
      // Transient errors (network, timeout, 5xx) should NOT clear tokens —
      // the refresh token may still be valid and retries may succeed.
      if (this.isNonRetryableError(errorObj)) {
        tokenService.clearTokens();
      }
      this.failRequestQueue(errorObj);

      // Re-throw to handle upstream
      throw errorObj;
    }
  }

  /**
   * Process queued requests after successful refresh.
   */
  private async processRequestQueue(): Promise<void> {
    authLogger.info(`[TokenRefreshService] Processing ${this.requestQueue.length} queued requests`);

    const queue = [...this.requestQueue];
    this.requestQueue = [];

    for (const { request, resolve, reject } of queue) {
      try {
        const result = await request();
        resolve(result);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(extractErrorMessage(error)));
      }
    }
  }

  /**
   * Fail all queued requests with the given error.
   */
  private failRequestQueue(error: Error): void {
    authLogger.info(`[TokenRefreshService] Failing ${this.requestQueue.length} queued requests`);

    const queue = [...this.requestQueue];
    this.requestQueue = [];

    for (const { reject } of queue) {
      reject(error);
    }
  }
}
